import React, { createContext, useContext, useEffect, useState } from 'react';
import posthog from 'posthog-js';

const PostHogContext = createContext();

export const usePostHog = () => {
  const context = useContext(PostHogContext);
  if (!context) {
    throw new Error('usePostHog must be used within a PostHogProvider');
  }
  return context;
};

export const PostHogProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize PostHog
    if (process.env.REACT_APP_POSTHOG_KEY && typeof window !== 'undefined') {
      posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
        // Disable automatic page view tracking since we'll do it manually
        capture_pageview: false,
        // Enable debug mode in development
        debug: process.env.NODE_ENV === 'development',
        // Only track in production or if explicitly enabled
        disabled: process.env.NODE_ENV === 'development' && !process.env.REACT_APP_POSTHOG_DEBUG,
        // Privacy settings
        respect_dnt: true,
        opt_out_capturing_by_default: false,
        // Session recording settings
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-sensitive]',
        },
        // Disable autocapture and heatmaps to prevent React synthetic event errors
        autocapture: false,
        capture_heatmaps: false,
        // Add loaded callback to wrap error-prone functions
        loaded: (posthog) => {
          // Wrap PostHog's internal functions to catch and suppress errors
          const originalCapture = posthog.capture;
          posthog.capture = function(...args) {
            try {
              return originalCapture.apply(this, args);
            } catch (error) {
              // Silently suppress PostHog tracking errors in production
              if (process.env.NODE_ENV === 'development') {
                console.warn('PostHog capture error (suppressed):', error.message);
              }
              return null;
            }
          };
        }
      });

      console.log('🔍 PostHog initialized successfully');
      setIsInitialized(true);
    } else {
      console.warn('⚠️ PostHog not initialized - missing REACT_APP_POSTHOG_KEY');
    }

    return () => {
      if (isInitialized) {
        posthog.reset();
      }
    };
  }, []);

  const trackEvent = (eventName, properties = {}) => {
    if (isInitialized) {
      posthog.capture(eventName, properties);
    }
  };

  const identifyUser = (userId, userProperties = {}) => {
    if (isInitialized) {
      posthog.identify(userId, userProperties);
    }
  };

  const trackPageView = (pageName, properties = {}) => {
    if (isInitialized) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        page_name: pageName,
        ...properties,
      });
    }
  };

  const resetUser = () => {
    if (isInitialized) {
      posthog.reset();
    }
  };

  const value = {
    posthog: isInitialized ? posthog : null,
    isInitialized,
    trackEvent,
    identifyUser,
    trackPageView,
    resetUser,
  };

  return (
    <PostHogContext.Provider value={value}>
      {children}
    </PostHogContext.Provider>
  );
};

export default PostHogProvider;
